import { lazy } from 'react';
import { createBrowserRouter } from 'react-router';
import SuspenseLoaderWrapper from '@/content/components/common/SuspenseLoaderWrapper';
import BaseLayout from '@/content/layouts/BaseLayout';
import DashboardLayout from '@/content/layouts/DashboardLayout';
import AuthenticatedRoute from '@/routes/AuthenticatedRoute';
import FRONTEND_ROUTES, { FRONTEND_ROUTES_KEYS } from '@/consts/frontendRoutes';
import UnAuthenticatedRoute from '@/routes/UnAuthenticatedRoute';
import { USER_MANAGEMENT_MODE } from '@/consts/userManagement';
import OrganizationSettings from '@/content/components/pages/OrganizationSettings';

// Pages
const Home = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/Home')));
const Dashboard = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/Dashboard')));
const AuthCallback = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/AuthCallback')));
const CompleteProfile = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/CompleteProfile')));
const SessionsDashboard = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/SessionsDashboard')));
const Session = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/Session')));
const OrganizationManagement = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/OrganizationManagement')));
const OrganizationManagementDetail = SuspenseLoaderWrapper(
  lazy(() => import('@/content/pages/OrganizationManagementDetail')),
);
const UserManagement = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/UserManagement')));
const LicenseManagement = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/LicenseManagement')));
const UserDetails = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/UserDetails')));
const MyMedia = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/MyMedia')));

// Profile pages
const ProfileLayout = SuspenseLoaderWrapper(lazy(() => import('@/content/layouts/ProfileLayout')));
const GeneralInformation = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/Profile/GeneralInformation')));
const UserPreferences = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/Profile/UserPreferences')));
const PrivacyData = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/Profile/PrivacyData')));
const License = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/Profile/License')));
// const DeviceLogin = SuspenseLoaderWrapper(lazy(() =>
//  import('@/content/pages/Profile/DeviceLogin')));
// const Billing = SuspenseLoaderWrapper(lazy(() =>
// import('@/content/pages/Profile/Billing')));
// const Notifications = SuspenseLoaderWrapper(lazy(() =>
//  import('@/content/pages/Profile/Notifications')));

const PrivacyPolicy = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/PrivacyPolicy')));
// Error pages
const Status404 = SuspenseLoaderWrapper(lazy(() => import('@/content/pages/Status404')));
const RouterErrorBoundary = SuspenseLoaderWrapper(lazy(() => import('@/content/components/common/RouterErrorBoundary')));

const {
  HOME, AUTH_CALLBACK, DASHBOARD, COMPLETE_PROFILE, USER_SESSIONS, SESSION,
  PROFILE, SELF_USER_MANAGEMENT, INDEPENDENT_USER_MANAGEMENT, USER_DETAILS, MY_MEDIA,
} = FRONTEND_ROUTES;

const router = createBrowserRouter([
  {
    // path: '',
    element: <UnAuthenticatedRoute><BaseLayout /></UnAuthenticatedRoute>,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: HOME,
        element: <Home />,
      },
      {
        path: AUTH_CALLBACK,
        element: <AuthCallback />,
      },
    ],
  },
  {
    // path: '',
    element: <AuthenticatedRoute><DashboardLayout withSidebar /></AuthenticatedRoute>,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: DASHBOARD,
        element: <Dashboard />,
      },
      {
        path: USER_SESSIONS,
        element: <SessionsDashboard />,
      },
      {
        path: FRONTEND_ROUTES.ORGANIZATION_MANAGEMENT_DETAIL,
        element: <OrganizationManagementDetail />,
      },
      {
        path: FRONTEND_ROUTES.ORGANIZATION_MANAGEMENT,
        element: <OrganizationManagement />,
      },
      {
        path: FRONTEND_ROUTES.ORGANIZATION_SETTINGS,
        element: <OrganizationSettings />,
      },
      {
        path: PROFILE,
        element: <ProfileLayout />,
        children: [
          { index: true, element: <GeneralInformation /> },
          { path: FRONTEND_ROUTES_KEYS.USER_PREFERENCES, element: <UserPreferences /> },
          { path: FRONTEND_ROUTES_KEYS.PRIVACY_DATA, element: <PrivacyData /> },
          { path: FRONTEND_ROUTES_KEYS.LICENSE, element: <License /> },
          // { path: 'device-login', element: <DeviceLogin /> },
          // { path: 'billing', element: <Billing /> },
          // { path: 'notifications', element: <Notifications /> },
        ],
      },
      {
        path: SELF_USER_MANAGEMENT,
        element: <UserManagement mode={USER_MANAGEMENT_MODE.SELF_ORG_USERS} />,
      },
      {
        path: INDEPENDENT_USER_MANAGEMENT,
        element: <UserManagement mode={USER_MANAGEMENT_MODE.INDEPENDENT_USERS} />,
      },
      {
        path: FRONTEND_ROUTES.LICENSE_MANAGEMENT,
        element: <LicenseManagement />,
      },
      {
        path: USER_DETAILS,
        element: <UserDetails />,
      },
      {
        path: MY_MEDIA,
        element: <MyMedia />,
      },
    ],
  },
  {
    // path: '',
    element: <AuthenticatedRoute><DashboardLayout /></AuthenticatedRoute>,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: COMPLETE_PROFILE,
        element: <CompleteProfile />,
      },
    ],
  },
  {
    // path: '',
    element: <AuthenticatedRoute><BaseLayout /></AuthenticatedRoute>,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: SESSION,
        element: <Session />,
      },
    ],
  },
  {
    // path: '',
    element: <BaseLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: FRONTEND_ROUTES.PRIVACY_POLICY,
        element: <PrivacyPolicy />,
      },
      {
        path: '*',
        element: <Status404 />,
      },
    ],
  },
], {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_skipActionErrorRevalidation: true,
  },
});

export default router;
